/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Tab, Tabs } from '@squiz/sds';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPONENT_SERVICE_STRINGS } from '../../../../Constants/Strings.constants';
import { ComponentServiceGetComponentPreviewDto, getComponentService } from '../../../../Services/component';
import { ComponentDetailsProps } from '../../ComponentDetails.props';

/**
 * Component details preview.
 * @param {ComponentDetailsProps} props Component details props.
 * @export
 * @returns {JSX.Element} The `<ComponentDetailsPreview />` component.
 */
export const ComponentDetailsPreview = (props: ComponentDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const { componentManifest, componentSetProps, name, namespace, selectedVersion } = props;

  // Component service
  const componentService = getComponentService();

  const [previewTabs, setPreviewTabs] = useState<Array<Tab>>([]);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const labels = {
    componentPreview: t(COMPONENT_SERVICE_STRINGS.componentPreview),
    noPreview: t(COMPONENT_SERVICE_STRINGS.noPreview),
  };

  useEffect(() => {
    const newPreviewTabs: Array<Tab> = [];
    let componentPreviews: Array<string> = [];
    if (componentManifest?.previews) {
      componentPreviews = Object.keys(componentManifest.previews);
      const componentType = componentManifest.type === 'edge' ? 'edge' : 'server';
      componentPreviews.forEach((previewKey: string, index: number) => {
        const previewDto: ComponentServiceGetComponentPreviewDto = {
          environmentVars: componentSetProps?.environmentVariables || {},
          name: name as string,
          namespace: namespace as string,
          previewKey,
          type: componentType,
          version: selectedVersion,
        };

        const previewUrl = componentService.createComponentPreviewUrl(previewDto);
        const tab: Tab = {
          content: (
            <div className="component-details__preview-content">
              <iframe src={previewUrl} title={'component-preview'} />
            </div>
          ),
          key: String(index) + previewKey + selectedVersion,
          label: previewKey,
        };
        newPreviewTabs.push(tab);
      });
    }

    setActiveTabIndex(0);
    setPreviewTabs(newPreviewTabs);
  }, [componentManifest, componentService, componentSetProps, name, namespace, selectedVersion]);

  if (previewTabs.length > 0) {
    return (
      <div className="component-details__preview">
        <h2>{labels.componentPreview}</h2>
        <div className="component-details__preview-container">
          <Tabs
            activeTabIndex={activeTabIndex}
            classes="component-details__preview-tabs"
            listLabel="Component previews"
            setActiveTabIndex={setActiveTabIndex}
            tabList={previewTabs}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="component-details__preview">
      <h2>{labels.componentPreview}</h2>
      <div className="component-details__preview-container">
        <div className="component-details_preview-empty">{labels.noPreview}</div>
      </div>
    </div>
  );
};
